import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { DemoLayerStates } from "../types/agricultural-lulc";
import {
  LULC_COLORS,
  LULC_CHANGE_COLORS,
  LULC_CHANGE_LABELS,
  CROP_COLORS,
  CROP_RASTER_COLORS,
  CROP_HARVEST_COLORS,
  FOREST_COLORS,
} from "../config/agricultural-lulc";
import { Label } from "@/components/ui/label";

interface LayerLegendProps {
  layers: DemoLayerStates;
  onClose?: () => void;
}

export function LayerLegend({ layers, onClose }: LayerLegendProps) {
  const sections: {
    title: string;
    items: { label: string; color: string }[];
  }[] = [];

  if (layers.lulc.visible) {
    if (layers.lulc.year === "change") {
      sections.push({
        title: "LULC Change",
        items: Object.entries(LULC_CHANGE_COLORS).map(([cls, color]) => ({
          label: LULC_CHANGE_LABELS[cls as keyof typeof LULC_CHANGE_LABELS],
          color,
        })),
      });
    } else {
      sections.push({
        title: `LULC ${layers.lulc.year}`,
        items: Object.entries(LULC_COLORS).map(([label, color]) => ({
          label,
          color,
        })),
      });
    }
  }

  if (layers.crops.visible) {
    const variant = layers.crops.variant ?? "parcel";
    const variantConfig: Record<
      string,
      { title: string; colors: Record<string, string> }
    > = {
      parcel: { title: "Crops (parcels · winter 2025)", colors: CROP_COLORS },
      crops: { title: "Crops (pixels · summer 2024)", colors: CROP_RASTER_COLORS },
      harvest: { title: "Harvest (pixels · summer 2024)", colors: CROP_HARVEST_COLORS },
    };
    const cfg = variantConfig[variant];
    sections.push({
      title: cfg.title,
      items: Object.entries(cfg.colors).map(([label, color]) => ({ label, color })),
    });
  }

  if (layers.forest.visible) {
    sections.push({
      title: "Forest",
      items: Object.entries(FOREST_COLORS).map(([label, color]) => ({
        label,
        color,
      })),
    });
  }

  if (sections.length === 0) return null;

  return (
    <Card className="backdrop-blur supports-backdrop-filter:bg-card/90">
      <CardHeader className="">
        <CardTitle>Legend</CardTitle>
        <CardAction>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="size-5"
              onClick={onClose}
            >
              <X className="" />
            </Button>
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        {sections.map((section) => (
          <div key={section.title}>
            <CardDescription className="font-semibold my-1">
              {section.title}
            </CardDescription>
            <div className="grid grid-cols-2 gap-x-4 gap-1.5">
              {section.items.map((item) => (
                <div key={item.label} className="flex items-center gap-1.5">
                  <div
                    className="size-4 rounded-sm"
                    style={{ backgroundColor: item.color }}
                  />
                  <Label className="text-muted-foreground truncate">
                    {item.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
